import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MappoolService, SeasonMappool } from '../services/MappoolService';

interface MappoolContextType {
    activeMappool: SeasonMappool | null;
    getMappool: (season: number) => Promise<string[]>;
    refreshMappool: () => Promise<void>;
    loading: boolean;
    error: string | null;
}

const MappoolContext = createContext<MappoolContextType | undefined>(undefined);

const CACHE_KEY_PREFIX = 'ncs_mappool_season_';
const CACHE_KEY_ACTIVE = 'ncs_mappool_active';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CachedMappool {
    data: SeasonMappool;
    timestamp: number;
}

export const MappoolProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeMappool, setActiveMappool] = useState<SeasonMappool | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Helper function to check if cache is valid
    const isCacheValid = (cachedItem: CachedMappool | null): boolean => {
        if (!cachedItem) return false;
        const now = Date.now();
        return (now - cachedItem.timestamp) < CACHE_TTL;
    };

    // Helper function to get from localStorage
    const getFromCache = (key: string): CachedMappool | null => {
        try {
            const cached = localStorage.getItem(key);
            if (!cached) return null;
            return JSON.parse(cached) as CachedMappool;
        } catch (error) {
            console.error('Error reading from cache:', error);
            return null;
        }
    };

    // Helper function to save to localStorage
    const saveToCache = (key: string, data: SeasonMappool): void => {
        try {
            const cachedItem: CachedMappool = {
                data,
                timestamp: Date.now()
            };
            localStorage.setItem(key, JSON.stringify(cachedItem));
        } catch (error) {
            console.error('Error saving to cache:', error);
        }
    };

    // Load active mappool on mount
    useEffect(() => {
        loadActiveMappool();
    }, []);

    const loadActiveMappool = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check cache first
            const cached = getFromCache(CACHE_KEY_ACTIVE);
            if (isCacheValid(cached)) {
                console.log('Using cached active mappool');
                setActiveMappool(cached!.data);
                setLoading(false);
                return;
            }

            // Fetch from API
            console.log('Fetching active mappool from API');
            const mappool = await MappoolService.fetchActiveMappool();
            setActiveMappool(mappool);
            saveToCache(CACHE_KEY_ACTIVE, mappool);
            setLoading(false);
        } catch (err) {
            console.error('Error loading active mappool:', err);
            setError('Failed to load mappool');
            setLoading(false);
        }
    };

    // Get mappool for a specific season (with caching)
    const getMappool = async (season: number): Promise<string[]> => {
        try {
            const cacheKey = `${CACHE_KEY_PREFIX}${season}`;

            // Check cache first
            const cached = getFromCache(cacheKey);
            if (isCacheValid(cached)) {
                console.log(`Using cached mappool for season ${season}`);
                return cached!.data.maps;
            }

            // Fetch from API
            console.log(`Fetching mappool for season ${season} from API`);
            const mappool = await MappoolService.fetchSeasonMappool(season);
            saveToCache(cacheKey, mappool);
            return mappool.maps;
        } catch (err) {
            console.error(`Error fetching mappool for season ${season}:`, err);

            // Fallback to hardcoded mappools if API fails
            console.warn(`Using fallback mappool for season ${season}`);
            const fallbackMappools: Record<number, string[]> = {
                1: ["Ancient", "Anubis", "Dust2", "Inferno", "Mirage", "Nuke", "Vertigo"],
                2: ["Ancient", "Anubis", "Dust2", "Inferno", "Mirage", "Nuke", "Train"],
                3: ["Ancient", "Dust2", "Inferno", "Mirage", "Nuke", "Overpass", "Train"],
            };

            return fallbackMappools[season] || fallbackMappools[2]; // Default to Season 2
        }
    };

    // Refresh mappool (clear cache and reload)
    const refreshMappool = async () => {
        try {
            console.log('Refreshing mappool cache');

            // Clear localStorage cache
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(CACHE_KEY_PREFIX) || key === CACHE_KEY_ACTIVE) {
                    localStorage.removeItem(key);
                }
            });

            // Reload active mappool
            await loadActiveMappool();
        } catch (err) {
            console.error('Error refreshing mappool:', err);
            setError('Failed to refresh mappool');
        }
    };

    return (
        <MappoolContext.Provider
            value={{
                activeMappool,
                getMappool,
                refreshMappool,
                loading,
                error
            }}
        >
            {children}
        </MappoolContext.Provider>
    );
};

// Custom hook to use the Mappool context
export const useMappoolContext = (): MappoolContextType => {
    const context = useContext(MappoolContext);
    if (!context) {
        throw new Error('useMappoolContext must be used within a MappoolProvider');
    }
    return context;
};
